import type { AuthState } from "../auth/authManager";
import {
  BB_DISCLAIMER_ACCEPTED_AT_KEY,
  BB_DISCLAIMER_ACCEPTED_KEY
} from "./bootOverlayManager";

const DEFAULT_REDIRECT_URL = "https://akidcalledbeast.com";
const DISCLAIMER_TITLE = "Photosensitivity Warning";
const DISCLAIMER_BODY = "This game features flashing lights, strobing effects, and high-contrast visuals. These may trigger seizures or discomfort for some players. If you have a history of seizures, consult a doctor before playing. Stop immediately if you experience dizziness, altered vision, eye strain, nausea, or disorientation.";

interface DisclaimerStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

interface PostLoginDisclaimerFlowControllerOptions {
  storage?: DisclaimerStorageLike | null;
  nowIso?: () => string;
  redirect?: (url: string) => void;
  redirectUrl?: string;
}

interface PostLoginDisclaimerManagerOptions extends PostLoginDisclaimerFlowControllerOptions {
  host: HTMLElement;
}

function resolveLocalStorage(): DisclaimerStorageLike | null {
  if (typeof window === "undefined" || window.localStorage == null) {
    return null;
  }
  return window.localStorage;
}

function safeStorageRead(storage: DisclaimerStorageLike | null, key: string): string | null {
  if (storage == null) {
    return null;
  }
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeStorageWrite(storage: DisclaimerStorageLike | null, key: string, value: string): void {
  if (storage == null) {
    return;
  }
  try {
    storage.setItem(key, value);
  } catch {
    // Ignore storage failures and continue without persistence.
  }
}

export function isRealAuthenticated(authState: Pick<AuthState, "user" | "profile">): boolean {
  const userId = authState.user?.id;
  if (userId == null || userId === "guest-local") {
    return false;
  }
  if (authState.profile == null) {
    return false;
  }
  return authState.profile.provider !== "guest";
}

export class PostLoginDisclaimerFlowController {
  private readonly storage: DisclaimerStorageLike | null;
  private readonly nowIso: () => string;
  private readonly redirect: (url: string) => void;
  private readonly redirectUrl: string;

  constructor(options: PostLoginDisclaimerFlowControllerOptions = {}) {
    this.storage = options.storage ?? resolveLocalStorage();
    this.nowIso = options.nowIso ?? (() => new Date().toISOString());
    this.redirect = options.redirect ?? ((url: string) => window.location.assign(url));
    this.redirectUrl = options.redirectUrl ?? DEFAULT_REDIRECT_URL;
  }

  hasAccepted(): boolean {
    return safeStorageRead(this.storage, BB_DISCLAIMER_ACCEPTED_KEY) === "1";
  }

  shouldShowAfterLogin(authState: Pick<AuthState, "user" | "profile">): boolean {
    if (!isRealAuthenticated(authState)) {
      return false;
    }
    return !this.hasAccepted();
  }

  accept(): void {
    safeStorageWrite(this.storage, BB_DISCLAIMER_ACCEPTED_KEY, "1");
    safeStorageWrite(this.storage, BB_DISCLAIMER_ACCEPTED_AT_KEY, this.nowIso());
  }

  decline(): void {
    this.redirect(this.redirectUrl);
  }
}

export class PostLoginDisclaimerManager {
  private readonly host: HTMLElement;
  private readonly flow: PostLoginDisclaimerFlowController;
  private readonly root: HTMLDivElement;
  private disposed = false;
  private attached = false;
  private visible = false;
  private readonly onWindowKeyDownCapture = (event: KeyboardEvent): void => {
    if (!this.visible) {
      return;
    }
    if (event.key === "F5" || (event.ctrlKey && event.key.toLowerCase() === "r")) {
      return;
    }
    if (event.target instanceof Node && this.root.contains(event.target)) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  constructor(options: PostLoginDisclaimerManagerOptions) {
    this.host = options.host;
    this.flow = new PostLoginDisclaimerFlowController({
      storage: options.storage,
      nowIso: options.nowIso,
      redirect: options.redirect,
      redirectUrl: options.redirectUrl
    });

    this.root = document.createElement("div");
    this.root.className = "post-login-disclaimer-root";
    this.root.style.display = "none";
    this.root.tabIndex = -1;
    this.root.setAttribute("aria-live", "polite");
    this.root.setAttribute("aria-label", "Photosensitivity warning");

    const consumeBackdropOnly = (event: Event): void => {
      if (event.target !== this.root) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    };
    this.root.addEventListener("pointerdown", consumeBackdropOnly, true);
    this.root.addEventListener("click", consumeBackdropOnly, true);
  }

  attach(): void {
    if (this.attached || this.disposed) {
      return;
    }
    this.host.appendChild(this.root);
    window.addEventListener("keydown", this.onWindowKeyDownCapture, true);
    this.attached = true;
  }

  maybeShowAfterLogin(authState: AuthState): void {
    if (this.disposed) {
      return;
    }
    if (!isRealAuthenticated(authState)) {
      if (this.visible) {
        this.close();
      }
      return;
    }
    if (this.visible || !this.flow.shouldShowAfterLogin(authState)) {
      return;
    }
    this.ensureAttached();
    this.renderPrompt();
    this.root.style.display = "flex";
    this.visible = true;
  }

  destroy(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    if (this.attached) {
      window.removeEventListener("keydown", this.onWindowKeyDownCapture, true);
    }
    this.root.remove();
  }

  private ensureAttached(): void {
    if (this.attached) {
      return;
    }
    this.attach();
  }

  private close(): void {
    this.root.style.display = "none";
    this.visible = false;
  }

  private renderPrompt(): void {
    this.root.replaceChildren();

    const section = document.createElement("section");
    section.className = "boot-disclaimer-screen";

    const card = document.createElement("div");
    card.className = "boot-disclaimer-card";

    const title = document.createElement("h1");
    title.className = "boot-disclaimer-title";
    title.textContent = DISCLAIMER_TITLE;

    const body = document.createElement("p");
    body.className = "boot-disclaimer-text";
    body.textContent = DISCLAIMER_BODY;

    const buttonRow = document.createElement("div");
    buttonRow.className = "boot-disclaimer-actions";

    const agreeButton = document.createElement("button");
    agreeButton.type = "button";
    agreeButton.className = "boot-btn boot-btn-primary";
    agreeButton.textContent = "Agree";
    agreeButton.addEventListener("click", () => {
      this.flow.accept();
      this.close();
    });

    const declineButton = document.createElement("button");
    declineButton.type = "button";
    declineButton.className = "boot-btn boot-btn-secondary";
    declineButton.textContent = "Decline";
    declineButton.addEventListener("click", () => {
      this.flow.decline();
    });

    buttonRow.appendChild(agreeButton);
    buttonRow.appendChild(declineButton);

    card.appendChild(title);
    card.appendChild(body);
    card.appendChild(buttonRow);
    section.appendChild(card);
    this.root.appendChild(section);
  }
}
